<template>
  <div class="w-full h-full min-h-0 overflow-hidden flex flex-col">
    <HeaderSelecter
      v-model="selectedDevice"
      class="shrink-0"
      :device-list="deviceList"
      @close="close"
    />

    <div v-if="isLoading" class="grid grid-cols-3 grid-rows-1 gap-4 py-8 w-full flex-1 min-h-0">
      <div class="flex flex-col gap-4 min-h-0 overflow-hidden">
        <Skeleton class="h-26 rounded-4xl" animation-type="shimmer" />
        <Skeleton class="h-18 rounded-4xl" animation-type="shimmer" />
        <Skeleton class="h-53 rounded-4xl" animation-type="shimmer" />
      </div>
      <div class="flex flex-col gap-4 min-h-0 overflow-hidden">
        <Skeleton class="h-48 rounded-4xl" animation-type="shimmer" />
        <Skeleton class="h-53 rounded-4xl" animation-type="shimmer" />
      </div>
      <div class="flex flex-col gap-4 min-h-0 overflow-hidden">
        <Skeleton class="h-105 rounded-4xl" animation-type="shimmer" />
      </div>
    </div>
    <div v-else class="grid grid-cols-3 grid-rows-1 gap-4 py-8 w-full flex-1 min-h-0">
      <div
        v-for="(col, colIdx) in displayColumns"
        :key="colIdx"
        class="flex flex-col gap-4 min-h-0 overflow-hidden"
      >
        <Card
          v-for="card in col"
          :key="card.title"
          :title="card.title"
          :data="card.data"
          :scrollable="card.scrollable"
          class="w-full"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
  import Card from './Card.vue'
  import { DeviceItem, useDeviceStore, useLocaleStore } from '@/stores'
  import { computed, onMounted, watch } from 'vue'
  import { useLoading } from '@/composables/useLoading/index.ts'
  import { useBaseData } from './composables/useBaseData'
  import { useDpiData } from './composables/useDpiData'
  import { useSoftware } from './composables/useSoftware'
  import { useKeyRemap } from './composables/useKeyRemap'
  import { useCfgData } from './composables/useCfgData'
  import { toast, Skeleton } from '@rapoo/element'
  import HeaderSelecter from './HeaderSelecter.vue'
  import { ref, type Ref } from 'vue'
  import { useScreenDisplay } from './composables/useScreenDisplay.ts'
  import { useLightBand } from './composables/useLightBand.ts'
  import { useScreenLogo } from './composables/useScreenLogo.ts'
  import { useKeyboardLighting } from './composables/useKeyboardLighting.ts'
  import { useRtData } from './composables/useRtData.ts'

  const { t } = useLocaleStore()

  const deviceStore = useDeviceStore()

  const emit = defineEmits(['close'])

  const props = defineProps<{
    type: string
    name: string
    item: DeviceItem
  }>()

  const selectedDevice = ref(props.item) as Ref<DeviceItem>
  const deviceList = computed(() => {
    const parentDevice = props.item.original.parentDevice
    if (!parentDevice) return [props.item]
    return [props.item, deviceStore.baseDeviceToDeviceItem(parentDevice)]
  })

  const { baseData, initBaseData } = useBaseData(selectedDevice)
  const { dpiData, initDpiData } = useDpiData(selectedDevice)
  const { softwareData, initSoftwareData } = useSoftware()
  const { keyRemapData, initKeyRemapData } = useKeyRemap(selectedDevice)
  const { cfgData, initCfgData } = useCfgData(selectedDevice)
  const { screenDisplayData, initScreenDisplay } = useScreenDisplay(selectedDevice)
  const { lightBandData, initLightBandData } = useLightBand(selectedDevice)
  const { screenLogoData, initScreenLogo } = useScreenLogo(selectedDevice)
  const { keyboardLightingCards, initKeyboardLightingData } = useKeyboardLighting(selectedDevice)
  const { rtData, initRtData } = useRtData(selectedDevice)

  // 骨架屏至少显示 200ms，避免加载过快时闪烁
  const { isLoading, withLoading } = useLoading(false, { minDurationMs: 200 })

  async function loadAllData() {
    await Promise.all([
      initBaseData(),
      initDpiData(),
      initSoftwareData(),
      initKeyRemapData(),
      initCfgData(),
      initScreenDisplay(),
      initLightBandData(),
      initScreenLogo(),
      initKeyboardLightingData(),
      initRtData(),
    ]).catch((_err) => {
      toast.error(t('toast.device.connectedFailed2'))
    })
  }

  // 首次加载
  onMounted(() => {
    withLoading(() => loadAllData())
  })

  // selectedDevice 切换时重新加载数据
  watch(selectedDevice, () => {
    console.log('selectedDevice changed, reloading data...')
    withLoading(() => loadAllData())
  })

  // 将卡片顺序填入 3 列：先填满第一列（从卡片数组顶部开始取），再依次填第二列、第三列
  const displayColumns = computed(() => {
    type CardData = {
      title: string
      data: ReadonlyArray<{ name: string; value: string | number; valueIcon?: string }>
      scrollable?: boolean
    }

    const cards: CardData[] = [
      baseData.value,
      dpiData.value,
      softwareData.value,
      keyRemapData.value,
      rtData.value,
      ...keyboardLightingCards.value,
      cfgData.value,
      screenDisplayData.value,
      lightBandData.value,
      screenLogoData.value,
    ].filter((c): c is NonNullable<typeof c> => !!c)

    const total = cards.length
    if (total === 0) return [[], [], []]

    // 计算每列分配数量：越靠前的列越多，至少每列 1 张
    const perCol = Math.ceil(total / 3)
    const col0 = Math.min(perCol, total)
    const rem1 = total - col0
    const col1 = Math.min(Math.ceil(rem1 / 2), rem1)
    const col2 = rem1 - col1

    // 按顺序切分
    let idx = 0
    const cols: CardData[][] = []
    for (const size of [col0, col1, col2]) {
      cols.push(cards.slice(idx, idx + size))
      idx += size
    }

    return cols
  })

  watch(
    () => props.item,
    (val, oVal) => {
      // 如果两个设备id完全相同
      if (val.id === oVal.id) return
      // 如果pid和vid完全相同，也不要自动关闭
      const device = val.original
      const pid1 = device.manifest.hardware.currentPid
      const vid = device.manifest.hardware.vendorId
      const oDevice = oVal.original
      const oPid = oDevice.manifest.hardware.currentPid
      const oVid = oDevice.manifest.hardware.vendorId

      if (pid1 === oPid && vid === oVid) return
      close()
    },
  )

  const close = () => {
    emit('close')
  }
</script>

<style lang="scss" scoped></style>
